import { resourceService } from "@libs/axios";
import { 
  fetchStudentModules, 
  fetchSubModules, 
  createMeetingExerciseView,
  fetchMeetingExerciseViews,
  updateExerciseTabs,
  fetchMeetingExerciseActivity,
  exportMeetingExerciseActivity,
  setViewResultExerciseId,
  setEdxDocumentBlockId,
  studentAnswerExerciseQuestion,
  meetingStudentAnswerQuestion,
  meetingStudentEndSubModuleSession,
  resetMeetingExerciseData,
  startExerciseSession,
  setSelectedExercise,
  deleteExerciseSession
} from "@store/actions/meetingActions";
import { 
  setLoading, 
  setStudentModules, 
  setError, 
  setSubModulesLoading,
  setSubModules,
  setSubModulesError,
  setCreatingExercise,
  setExerciseError,
  setExerciseViewsLoading,
  setMeetingExerciseViews,
  setExerciseViewsError,
  setExerciseTabs,
  setExerciseActivityLoading,
  setExerciseActivityData,
  setExerciseActivityError,
  setExportExerciseActivityLoading,
  setViewResultExerciseId as setViewResultExerciseIdReducer,
  setEdxDocumentBlockId as setEdxDocumentBlockIdReducer,
  setStudentAnswerExerciseQuestionLoading,
  setStudentAnswerExerciseQuestion,
  setStudentAnswerExerciseQuestionError,
  setMeetingSessionSubmoduleResult,
  resetMeetingExerciseData as resetMeetingExerciseDataReducer,
  setExerciseSessionLoading,
  setExerciseSessionData,
  setExerciseSessionError,
  setSelectedExercise as setSelectedExerciseReducer
} from "@store/reducers/meetingReducer";
import { call, put, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import { ResponseType } from "@store/index";
import { checkXlsxBlob, readErrorMessage, triggerBlobDownload, XlsxBlobCheck } from "@utils/FileDownloadUtils";
const API_URL = process.env.REACT_APP_API_URL;

function* fetchStudentModulesSaga() {
  try {
    yield put(setLoading(true));
    
    // Call the API to fetch student modules
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + '/api/modules/?page=1&page_size=100&status=1'
    );

    if (response.status === 200) {
      yield put(setStudentModules(response?.data?.results));
    } else {
      yield put(setError("Failed to fetch student modules"));
    }
  } catch (error: any) {
    yield put(setError("An error occurred while fetching student modules"));
    console.error("Error fetching student modules:", error);
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchSubModulesSaga(action: ReturnType<typeof fetchSubModules>) {
  try {
    yield put(setSubModulesLoading(true));
    
    const { moduleId } = action.payload;
    
    // Call the API to fetch submodules for the specified module ID
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/sub_modules/?module_id=${moduleId}`
    );

    if (response.status === 200) {
      yield put(setSubModules(response.data));
    } else {
      yield put(setSubModulesError("Failed to fetch submodules"));
    }
  } catch (error: any) {
    yield put(setSubModulesError("An error occurred while fetching submodules"));
    console.error("Error fetching submodules:", error);
  } finally {
    yield put(setSubModulesLoading(false));
  }
}

function* createMeetingExerciseViewSaga(action: ReturnType<typeof createMeetingExerciseView>) {
  try {
    yield put(setCreatingExercise(true));
    
    const { name, meeting_id, questions, school_id, class_id, onSuccess, onError } = action.payload;
    
    // Validate exercise name
    if (!name || name.trim() === '') {
      console.error('Exercise name is null or empty');
      toast.error("Tên bài tập không được để trống");
      if (onError) {
        yield call(onError);
      }
      return;
    }
    
    console.log('Creating exercise with data:', { 
      name, meeting_id, school_id, class_id, 
      questionsCount: questions.length 
    });
    
    const requestData = {
      name: name.trim(),
      meeting_id,
      school: school_id || 1, // Provide fallback if null
      class_instance: class_id || 1,   // Provide fallback if null
      lms_zoom_exercise_questions: questions.map(q => ({
        block_id_question: q.block_id_question,
        name_question: q.name_question,
        type_question: q.type_question
      }))
    };
    
    // Call the API to create meeting exercise view
    const response: ResponseType = yield call(
      resourceService.post,
      `${API_URL}/api/create_meeting_exercise_view/`,
      requestData
    );

    if (response.status === 200 || response.status === 201) {
      console.log('Exercise created successfully:', response.data);
      
      // After successful creation, fetch the updated list of meeting exercise views
      yield put(fetchMeetingExerciseViews({ meeting_id }));
      
      if (onSuccess) {
        yield call(onSuccess);
      }
    } else {
      console.error('Failed to create exercise:', response);
      toast.error("Có lỗi xảy ra, Tạo bài tập không thành công");
      yield put(setExerciseError("Failed to create meeting exercise view"));
      if (onError) {
        yield call(onError);
      }
    }
  } catch (error: any) {
    console.error("Error creating meeting exercise view:", error);
    toast.error(error?.response?.data?.error ?? "Có lỗi xảy ra, Tạo bài tập không thành công");
    yield put(setExerciseError("An error occurred while creating meeting exercise view"));
    if (action.payload.onError) {
      yield call(action.payload.onError);
    }
  } finally {
    yield put(setCreatingExercise(false));
  }
}

function* fetchMeetingExerciseViewsSaga(action: ReturnType<typeof fetchMeetingExerciseViews>) {
  try {
    yield put(setExerciseViewsLoading(true));
    
    const { meeting_id } = action.payload;
    
    // Call the API to fetch meeting exercise views
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/get_meeting_exercise_view/?meeting_id=${meeting_id}`
    );

    if (response.status === 200) {
      yield put(setMeetingExerciseViews(response.data));
    } else {
      yield put(setExerciseViewsError("Failed to fetch meeting exercise views"));
    }
  } catch (error: any) {
    yield put(setExerciseViewsError("An error occurred while fetching meeting exercise views"));
    console.error("Error fetching meeting exercise views:", error);
  } finally {
    yield put(setExerciseViewsLoading(false));
  }
}

function* fetchMeetingExerciseActivitySaga(action: ReturnType<typeof fetchMeetingExerciseActivity>) {
  try {
    yield put(setExerciseActivityLoading(true));
    
    const { meeting_id, exercise_id } = action.payload;
    
    // Call the API to fetch exercise activity data
    const response: ResponseType = yield call(
      resourceService.get,
      `${process.env.REACT_APP_API_URL}/api/get_meeting_exercise_activity/?meeting_id=${meeting_id}&exercise_id=${exercise_id}`
    );

    if (response.status === 200) {
      yield put(setExerciseActivityData(response.data));
    } else {
      yield put(setExerciseActivityError("Failed to fetch exercise activity data"));
    }
  } catch (error: any) {
    yield put(setExerciseActivityError("An error occurred while fetching exercise activity data"));
    console.error("Error fetching exercise activity data:", error);
  } finally {
    yield put(setExerciseActivityLoading(false));
  }
}

function* exportMeetingExerciseActivitySaga(action: ReturnType<typeof exportMeetingExerciseActivity>) {
  try {
    yield put(setExportExerciseActivityLoading(true));

    const { meeting_id, exercise_id } = action.payload;
    const params = new URLSearchParams({ meeting_id: String(meeting_id) });
    if (exercise_id) params.set('exercise_id', String(exercise_id));

    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/export_meeting_exercise_activity/?${params.toString()}`,
      { responseType: 'blob' }
    );

    // Kiểm tra nội dung có thật là file .xlsx (200 + body lỗi thì báo lỗi, không tải file hỏng)
    const check: XlsxBlobCheck = yield call(checkXlsxBlob, response.data);
    if (response.status === 200 && check.ok) {
      const contentDisposition = response.headers?.['content-disposition'];
      const defaultFilename = exercise_id
        ? `ket_qua_bai_tap_${meeting_id}_${exercise_id}.xlsx`
        : `ket_qua_bai_tap_${meeting_id}.xlsx`;
      triggerBlobDownload(response.data, defaultFilename, contentDisposition);
    } else {
      const { message } = check;
      toast.error(message ? `Xuất kết quả bài tập thất bại: ${message}` : 'Xuất kết quả bài tập thất bại.');
    }
  } catch (error: any) {
    console.error("Error exporting exercise activity:", error);
    // 401 đã được interceptor của resourceService toast + chuyển về trang đăng nhập
    if (error?.response?.status !== 401) {
      const message: string | undefined = yield call(readErrorMessage, error?.response?.data);
      toast.error(message ? `Xuất kết quả bài tập thất bại: ${message}` : 'Xuất kết quả bài tập thất bại.');
    }
  } finally {
    yield put(setExportExerciseActivityLoading(false));
  }
}

function* setViewResultExerciseIdSaga(action: ReturnType<typeof setViewResultExerciseId>) {
  try {
    const { id: exerciseId, name: exerciseName } = action.payload;
    console.log('Setting view result exercise ID in saga:', exerciseId, exerciseName);
    
    // Update the Redux store using the reducer
    yield put(setViewResultExerciseIdReducer(action.payload));
    
  } catch (error: any) {
    console.error('Error setting view result exercise ID:', error);
  }
}

function* setEdxDocumentBlockIdSaga(action: ReturnType<typeof setEdxDocumentBlockId>) {
  try {
    yield put(setEdxDocumentBlockIdReducer(action.payload));
  } catch (error: any) {
    console.error('Error setting EDX document block ID:', error);
  }
}

function* studentAnswerExerciseQuestionSaga(action: ReturnType<typeof studentAnswerExerciseQuestion>) {
  try {
    yield put(setStudentAnswerExerciseQuestionLoading(true));
    
    const { questionId } = action.payload;
    
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/student_answer_execersise_questions/${questionId}/`
    );

    if (response.status === 200) {
      yield put(setStudentAnswerExerciseQuestion(response.data));
    } else {
      yield put(setStudentAnswerExerciseQuestionError("Failed to fetch sub module detail"));
    }
  } catch (error: any) {
    yield put(setStudentAnswerExerciseQuestionError("An error occurred while fetching sub module detail"));
    console.error("Error fetching sub module detail:", error);
  } finally {
    yield put(setStudentAnswerExerciseQuestionLoading(false));
  }
}

function* meetingStudentAnswerQuestionSaga(action: ReturnType<typeof meetingStudentAnswerQuestion>) {
  try {
    yield put(setStudentAnswerExerciseQuestionLoading(true));
    
    const { questionId } = action.payload;
    
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/student_answer_execersise_questions/${questionId}/`
    );

    if (response.status === 200) {
      yield put(setStudentAnswerExerciseQuestion(response.data));
    } else {
      yield put(setStudentAnswerExerciseQuestionError("Failed to fetch sub module detail"));
      toast.error("Lỗi khi gửi câu trả lời");
    }
  } catch (error: any) {
    yield put(setStudentAnswerExerciseQuestionError("An error occurred while answering question"));
    console.error("Error answering question:", error);
    toast.error("Lỗi khi gửi câu trả lời");
  } finally {
    yield put(setStudentAnswerExerciseQuestionLoading(false));
  }
}

function* meetingStudentEndSubModuleSessionSaga(action: ReturnType<typeof meetingStudentEndSubModuleSession>) {
  try {
    yield put(setStudentAnswerExerciseQuestionLoading(true));
    
    const { sessionId } = action.payload;
    
    const response: ResponseType = yield call(
      resourceService.post,
      `${API_URL}/api/student_end_sub_module_session/`,
      { session_id: sessionId }
    );

    if (response.status === 200) {
      yield put(setMeetingSessionSubmoduleResult(response.data));
    } else {
      toast.error("Lỗi khi kết thúc bài thi");
    }
  } catch (error: any) {
    console.error("Error ending session:", error);
    toast.error("Lỗi khi kết thúc bài thi");
  } finally {
    yield put(setStudentAnswerExerciseQuestionLoading(false));
  }
}

function* resetMeetingExerciseDataSaga() {
  try {
    yield put(resetMeetingExerciseDataReducer());
  } catch (error: any) {
    console.error("Error resetting meeting exercise data:", error);
  }
}

function* startExerciseSessionSaga(action: ReturnType<typeof startExerciseSession>) {
  try {
    yield put(setExerciseSessionLoading(true));
    
    const { meeting_id, exercise_id } = action.payload;
    
    const response: ResponseType = yield call(
      resourceService.post,
      `${API_URL}/api/student_get_execersise_sessions`,
      { 
        meeting_id, 
        exercise_id 
      }
    );

    if (response.status === 200) {
      yield put(setExerciseSessionData(response.data));
    } else if (response.status === 404) {
      yield put(setExerciseSessionError("Bạn chưa có phiên làm bài tập nào"));
      toast.info("Bạn chưa có phiên làm bài tập nào");
    } else {
      yield put(setExerciseSessionError("Failed to start exercise session"));
      toast.error("Lỗi khi bắt đầu phiên bài tập");
    }
  } catch (error: any) {
    if (error?.status === 404) {
      toast.info("Bạn chưa có phiên làm bài tập nào");
      yield put(setExerciseSessionError("Bạn chưa có phiên làm bài tập nào"));
    } else {
      yield put(setExerciseSessionError("An error occurred while starting exercise session"));
      console.error("Error starting exercise session:", error);
      toast.error("Lỗi khi bắt đầu phiên bài tập");
    }
  } finally {
    yield put(setExerciseSessionLoading(false));
  }
}

function* setSelectedExerciseSaga(action: ReturnType<typeof setSelectedExercise>) {
  try {
    const { id, data } = action.payload;
    console.log('Setting selected exercise in saga:', { id, data });

    // Update the Redux store using the reducer
    yield put(setSelectedExerciseReducer({ id, data }));

    // Offline meeting: selectedExercise in Redux opens fullscreen ExerciseTab modal.
    // Online Zoom meeting: open exercise in browser-style tab.
    const isOfflineMeeting = window.location.pathname.includes('/offline-meeting');
    if (!isOfflineMeeting && (window as any).addExerciseTab && data) {
      (window as any).addExerciseTab(data);
    }
  } catch (error: any) {
    console.error('Error setting selected exercise:', error);
  }
}


function* deleteExerciseSessionSaga(action: ReturnType<typeof deleteExerciseSession>) {
  try {    
    yield put(setLoading(true));
    const { meeting_id, exercise_id } = action.payload;
    
    const response: ResponseType = yield call(
      resourceService.delete,
      `${API_URL}/api/create_meeting_exercise_view/?meeting_id=${meeting_id}&exercise_id=${exercise_id}`,
    );

    if (response.status === 204) {
      toast.success("Xóa bài tập thành công");
      yield put(fetchMeetingExerciseViews({ meeting_id }));
    }
  } catch (error: any) {
    toast.error( error?.response?.data?.error ?? "Có lỗi xảy ra, Không thể xóa được bài tập");
  } finally {
    yield put(setLoading(false));
  }
}

export default function* meetingSaga() {
  yield takeLatest(fetchStudentModules.type, fetchStudentModulesSaga);
  yield takeLatest(fetchSubModules.type, fetchSubModulesSaga);
  yield takeLatest(createMeetingExerciseView.type, createMeetingExerciseViewSaga);
  yield takeLatest(fetchMeetingExerciseViews.type, fetchMeetingExerciseViewsSaga);
  yield takeLatest(fetchMeetingExerciseActivity.type, fetchMeetingExerciseActivitySaga);
  yield takeLatest(exportMeetingExerciseActivity.type, exportMeetingExerciseActivitySaga);
  yield takeLatest(setViewResultExerciseId.type, setViewResultExerciseIdSaga);
  yield takeLatest(setEdxDocumentBlockId.type, setEdxDocumentBlockIdSaga);
  yield takeLatest(setSelectedExercise.type, setSelectedExerciseSaga);
  yield takeLatest(studentAnswerExerciseQuestion.type, studentAnswerExerciseQuestionSaga);
  yield takeLatest(meetingStudentAnswerQuestion.type, meetingStudentAnswerQuestionSaga);
  yield takeLatest(meetingStudentEndSubModuleSession.type, meetingStudentEndSubModuleSessionSaga);
  yield takeLatest(resetMeetingExerciseData.type, resetMeetingExerciseDataSaga);
  yield takeLatest(startExerciseSession.type, startExerciseSessionSaga);
  yield takeLatest(deleteExerciseSession.type, deleteExerciseSessionSaga);

} 